/**
 * The clinic app shell, drawn from Brian's 23 September mockups (clinic
 * dashboard, RETAIN list, RETAIN patient): a paper sidebar with the MCOS
 * wordmark, thin line icons, count badges and the closing line "A longer,
 * healthier life is a more vibrant one.", and a top header with patient
 * search, the Physician Alerts box, the signed-in clinician and the strapline
 * "Better health gives you more good days."
 *
 * His spec for this part: "Persistent left navigation, white/paper
 * background. Thin divider between sidebar and content. Top header with
 * search, notifications, clinician avatar/name." and "Active navigation row
 * gets pale green fill using #DCE7DE. Active icon/text in dark green. Add a
 * thin green vertical indicator on the selected item. All icons should use
 * the same thin-line style."
 *
 * Differences from the mockups, each deliberate and listed in the redesign
 * artifact: Clinical Twin is not in the clinic navigation (Brian: "Remove the
 * Clinical Twin tab from the clinic navigation"; it lives in the patient app),
 * and "Help" is left out until there is somewhere real for it to lead.
 *
 * Only the main column scrolls; the sidebar and header stay put.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { IdleSignOut } from './IdleSignOut';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../lib/api';
import { useAuth, type Role } from '../lib/auth';
import { useRealtime, type NotificationCounts } from '../lib/realtime';
import {
  IconAlert,
  IconAnalytics,
  IconCalendar,
  IconCheck,
  IconChevron,
  IconChevronDown,
  IconClipboard,
  IconClose,
  IconDashboard,
  IconHeart,
  IconHelp,
  IconLabs,
  IconLeaf,
  IconLogout,
  IconMenu,
  IconMessage,
  IconPatients,
  IconPill,
  IconReports,
  IconSearch,
  IconSettings,
  IconShield,
} from './icons';

/* ------------------------------------------------------------------ *
 * Page title in the header
 * ------------------------------------------------------------------ */

interface HeaderTitle {
  title: string;
  subtitle?: string;
}

const HeaderContext = createContext<(value: HeaderTitle | null) => void>(() => undefined);

/**
 * The clinic dashboard mockup prints its title in the header row, beside the
 * search, rather than in the page body ("Clinic Dashboard / Manage your
 * patients. Personalize treatment. Better outcomes."). A page that wants that
 * calls this; every other page keeps its own heading in the body, as the
 * RETAIN mockups do.
 */
export function useShellTitle(title: string, subtitle?: string): void {
  const set = useContext(HeaderContext);
  useEffect(() => {
    set({ title, subtitle });
    return () => set(null);
  }, [set, title, subtitle]);
}

/* ------------------------------------------------------------------ *
 * Navigation
 * ------------------------------------------------------------------ */

type IconComponent = (props: { size?: number; className?: string }) => ReactElement;

interface NavItem {
  to: string;
  label: string;
  icon: IconComponent;
  roles?: Role[];
  count?: 'newPatients' | 'retainCheckIns' | 'planQuestions' | 'readyToPrescribe' | 'signQueue' | 'messages' | 'pharmacies' | 'pharmacyProducts';
}

interface NavCounts {
  newPatients: number;
  retainCheckIns: number;
  planQuestions: number;
  readyToPrescribe: number;
  /** S-23: plans waiting for this prescriber's signature. */
  signQueue?: number;
  /** S-13: patient conversations waiting on the clinic. */
  messages?: number;
  /** S-20: unread price change notices from a linked pharmacy. */
  pharmacies?: number;
  /** S-21: pharmacy products waiting for Dr. Joe to confirm. */
  pharmacyProducts?: number;
}

/** The clinic rail in the mockup's order, less the two items noted above. */
const NAV: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: IconDashboard },
  { to: '/patients', label: 'Patients', icon: IconPatients },
  { to: '/new-patients', label: 'New Patients', icon: IconClipboard, count: 'newPatients' },
  { to: '/retain', label: 'RETAIN Check-Ins', icon: IconHeart, count: 'retainCheckIns' },
  { to: '/treatment-plans', label: 'MCOS Treatment Plans', icon: IconMedkitLike },
  { to: '/ready-to-prescribe', label: 'Ready to Prescribe', icon: IconHelp, roles: ['ADMIN', 'CLINICIAN'], count: 'readyToPrescribe' },
  { to: '/labs', label: 'Labs', icon: IconLabs },
  { to: '/reports', label: 'Reports', icon: IconAnalytics },
  { to: '/messages', label: 'Messages', icon: IconMessage, count: 'messages' },
  { to: '/resources', label: 'Resources', icon: IconReports },
];

const NAV_BOTTOM: NavItem[] = [
  { to: '/settings/credentials', label: 'Credentials', icon: IconShield, roles: ['ADMIN', 'CLINICIAN'] },
  // S-20: "Settings > Pharmacies", beside Credentials, the other provider-only settings page.
  { to: '/settings/pharmacies', label: 'Pharmacies', icon: IconPill, roles: ['ADMIN', 'CLINICIAN'], count: 'pharmacies' },
  { to: '/settings', label: 'Settings', icon: IconSettings, roles: ['ADMIN'] },
];

/** The mockup's "MCOS Treatment Plans" glyph: a kit with a pulse line. */
function IconMedkitLike({ size = 18, className = '' }: { size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <rect x="4" y="6.6" width="16" height="13.4" rx="1.8" />
      <path d="M9 6.6V4.6h6v2M7.4 13.4h2.4l1.4-2.4 1.8 4.2 1.4-1.8h2.2" />
    </svg>
  );
}

/**
 * The two platform roles see a different rail entirely: cross-clinic
 * surfaces, never the clinic-scoped views.
 */
const PLATFORM_NAV: NavItem[] = [
  { to: '/clinical', label: 'MCOS Clinical', icon: IconHeart, roles: ['MEDICAL_DIRECTOR'] },
  { to: '/sign-queue', label: 'Sign queue', icon: IconClipboard, roles: ['MEDICAL_DIRECTOR'], count: 'signQueue' },
  // S-21, the client's 8 October answer: Dr. Joe confirms new pharmacy products in his portal.
  { to: '/pharmacy-products', label: 'Pharmacy products', icon: IconPill, roles: ['MEDICAL_DIRECTOR'], count: 'pharmacyProducts' },
  { to: '/platform', label: 'Overview', icon: IconAnalytics, roles: ['SUPERADMIN'] },
  { to: '/platform/clinics', label: 'Clinics', icon: IconPatients, roles: ['SUPERADMIN'] },
  { to: '/platform/subscriptions', label: 'Subscriptions', icon: IconReports, roles: ['SUPERADMIN'] },
  { to: '/platform/plan-settings', label: 'Treatment plans', icon: IconPill, roles: ['SUPERADMIN'] },
  { to: '/platform/legal', label: 'Legal', icon: IconShield, roles: ['SUPERADMIN'] },
  { to: '/platform/pharmacies', label: 'Pharmacies', icon: IconLabs, roles: ['SUPERADMIN'] },
  { to: '/platform/question-bank', label: 'Question bank', icon: IconHelp, roles: ['SUPERADMIN', 'MEDICAL_DIRECTOR'] },
  { to: '/platform/providers', label: 'Providers', icon: IconLabs, roles: ['SUPERADMIN', 'MEDICAL_DIRECTOR'] },
  { to: '/settings/credentials', label: 'Credentials', icon: IconShield, roles: ['MEDICAL_DIRECTOR'] },
];

/**
 * The sidebar badges. The socket invalidates this the moment a count moves;
 * the interval is only the fallback for a connection that has dropped.
 */
function useNavCounts(enabled: boolean) {
  return useQuery({
    queryKey: ['nav-counts'],
    queryFn: () => api.get<NavCounts>('/dashboard/nav-counts'),
    enabled,
    refetchInterval: 120_000,
    retry: false,
  });
}

const ROLE_LABEL: Record<Role, string> = {
  ADMIN: 'Clinic Admin',
  CLINICIAN: 'Clinician',
  STAFF: 'Clinic Staff',
  SUPERADMIN: 'MCOS Platform',
  MEDICAL_DIRECTOR: 'Medical Director',
};

/* ------------------------------------------------------------------ *
 * The shell
 * ------------------------------------------------------------------ */

export function Shell() {
  const { user, can, logout } = useAuth();
  const signOutIdle = useCallback(() => void logout(), [logout]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [header, setHeader] = useState<HeaderTitle | null>(null);
  const location = useLocation();

  useEffect(() => setMobileOpen(false), [location.pathname]);

  const isPlatform = user?.role === 'SUPERADMIN' || user?.role === 'MEDICAL_DIRECTOR';
  const visible = (items: NavItem[]) => items.filter((item) => !item.roles || can(...item.roles));
  const nav = isPlatform ? visible(PLATFORM_NAV) : visible(NAV);
  const bottom = isPlatform ? [] : visible(NAV_BOTTOM);

  const clinicCounts = useNavCounts(!isPlatform);
  // Dr. Joe's rail has two badges, his Sign queue (S-23) and the pharmacy products waiting for him (S-21).
  const signCount = useQuery({
    queryKey: ['sign-queue-count'],
    queryFn: () => api.get<{ count: number }>('/signoff/queue/count'),
    enabled: user?.role === 'MEDICAL_DIRECTOR',
    refetchInterval: 120_000,
    retry: false,
  });
  const productCount = useQuery({
    queryKey: ['pharmacy-products-count'],
    queryFn: () => api.get<{ count: number }>('/clinical/pharmacy-mappings/count'),
    enabled: user?.role === 'MEDICAL_DIRECTOR',
    refetchInterval: 120_000,
    retry: false,
  });
  const counts = { data: isPlatform ? { newPatients: 0, retainCheckIns: 0, planQuestions: 0, readyToPrescribe: 0, signQueue: signCount.data?.count ?? 0, pharmacyProducts: productCount.data?.count ?? 0 } : clinicCounts.data };
  useRealtime(Boolean(user) && !isPlatform);

  return (
    <HeaderContext.Provider value={setHeader}>
      <IdleSignOut onSignOut={signOutIdle} />
      <div className="app-scale h-app flex overflow-hidden bg-paper">
        <aside className="no-scrollbar hidden lg:flex lg:flex-col w-[256px] shrink-0 h-app overflow-y-auto border-r border-hairline bg-[#f8f9f3]">
          <Sidebar nav={nav} bottom={bottom} counts={counts.data ?? null} />
        </aside>

        {mobileOpen && (
          <div className="lg:hidden fixed inset-0 z-40 flex">
            <button
              type="button"
              aria-label="Close navigation"
              className="absolute inset-0 bg-ink/40"
              onClick={() => setMobileOpen(false)}
            />
            <aside className="no-scrollbar relative w-[260px] flex flex-col overflow-y-auto bg-[#f8f9f3] shadow-pop">
              <button
                type="button"
                aria-label="Close navigation"
                className="absolute top-4 right-3 p-1.5 rounded-lg text-navy hover:bg-ink/5"
                onClick={() => setMobileOpen(false)}
              >
                <IconClose size={18} />
              </button>
              <Sidebar nav={nav} bottom={bottom} counts={counts.data ?? null} />
            </aside>
          </div>
        )}

        <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
          <TopHeader header={header} onOpenMenu={() => setMobileOpen(true)} showBell={!isPlatform} />
          <main className="flex-1 min-w-0 overflow-y-auto px-4 sm:px-6 lg:px-8 pt-6 pb-10">
            {/* Keyed on the path so each page fades in when you move to it. */}
            <div key={location.pathname} className="mcos-fade mx-auto max-w-[1440px]">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </HeaderContext.Provider>
  );
}

/* ------------------------------------------------------------------ *
 * Sidebar
 * ------------------------------------------------------------------ */

function Wordmark() {
  return (
    <div>
      <div className="font-display text-[40px] leading-none text-navy tracking-[-0.01em]" style={{ fontWeight: 480 }}>
        MCOS
      </div>
      <div className="mt-2 whitespace-nowrap text-[8.6px] font-medium uppercase text-ink-muted tracking-[0.1em]">
        Metabolic health. Real progress.
      </div>
    </div>
  );
}

function Sidebar({ nav, bottom, counts }: { nav: NavItem[]; bottom: NavItem[]; counts: NavCounts | null }) {
  return (
    <>
      <div className="pl-[28px] pr-3 pt-5 pb-4 border-b border-hairline">
        <Link to="/" aria-label="MCOS dashboard">
          <Wordmark />
        </Link>
      </div>

      <nav className="pt-2" aria-label="Main">
        {nav.map((item) => (
          <SidebarLink key={item.to} item={item} counts={counts} />
        ))}
      </nav>

      {bottom.length > 0 && (
        <>
          <div className="ml-6 mr-8 my-2 border-t border-hairline" />
          <nav aria-label="Practice">
            {bottom.map((item) => (
              <SidebarLink key={item.to} item={item} counts={counts} />
            ))}
          </nav>
        </>
      )}

      {/* The mockup's closing line, in Fraunces italic under a leaf. */}
      <div className="mt-auto pl-[30px] pr-6 pt-6 pb-6 [@media(max-height:600px)]:hidden">
        <svg width="28" height="28" viewBox="0 0 24 24" aria-hidden="true" className="text-forest">
          <path d="M4.6 19.4C4.6 10.6 10.2 5 19.6 4.4 19 13.8 13.4 19.4 4.6 19.4Z" fill="currentColor" />
          <path d="M4.6 19.4 12.6 11.4" stroke="#f8f9f3" strokeWidth="1.3" strokeLinecap="round" />
        </svg>
        <p className="mt-2 font-display italic text-[21px] leading-[1.25] text-navy" style={{ fontWeight: 420 }}>
          A longer, healthier life is a more vibrant one.
        </p>
        <div className="mt-3 h-px w-8 bg-navy/70" />
      </div>
    </>
  );
}

function SidebarLink({ item, counts }: { item: NavItem; counts: NavCounts | null }) {
  const Icon = item.icon;
  const count = item.count && counts ? counts[item.count] ?? 0 : 0;
  return (
    <NavLink
      to={item.to}
      // Section roots match only themselves, so Overview is not lit on every platform page.
      end={item.to === '/' || item.to === '/platform' || item.to === '/settings'}
      className={({ isActive }) =>
        `relative flex h-[42px] items-center gap-[14px] pl-[26px] pr-[16px] text-[14.5px] whitespace-nowrap transition-colors ${
          isActive ? 'bg-mint text-forest font-semibold' : 'text-navy hover:bg-ink/[0.035]'
        }`
      }
    >
      {({ isActive }) => (
        <>
          {isActive && <span aria-hidden="true" className="absolute left-0 top-0 bottom-0 w-[3px] bg-forest" />}
          <Icon size={20} className={isActive ? 'text-forest' : 'text-[#23413c]'} />
          <span className="min-w-0 flex-1 overflow-hidden text-ellipsis">{item.label}</span>
          {/* A zero badge says "nothing here", which no badge already says.
              Brian, 26 Sept: "RED for retain, and something positive for New
              Patient", so the two counts never read as the same thing. */}
          {count > 0 && (
            <span
              className={`grid h-[22px] min-w-[22px] place-items-center rounded-full px-1.5 text-[12px] font-semibold leading-none text-white ${
                item.count === 'retainCheckIns' ? 'bg-alert' : 'bg-forest'
              }`}
            >
              {count}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

/* ------------------------------------------------------------------ *
 * Header
 * ------------------------------------------------------------------ */

function TopHeader({
  header,
  onOpenMenu,
  showBell,
}: {
  header: HeaderTitle | null;
  onOpenMenu: () => void;
  showBell: boolean;
}) {
  return (
    <header className="shrink-0 border-b border-hairline/70 bg-paper">
      <div className="flex items-center gap-2 sm:gap-4 lg:gap-6 px-4 sm:px-6 lg:px-8 min-h-[72px] sm:min-h-[88px] py-3">
        <button
          type="button"
          aria-label="Open navigation"
          className="lg:hidden p-2 -ml-2 rounded-lg text-navy hover:bg-ink/5"
          onClick={onOpenMenu}
        >
          <IconMenu size={22} />
        </button>

        <div className="min-w-0 flex-1">
          {header && (
            <>
              <h1 title={header.title} className="font-display text-[clamp(20px,5.6vw,26px)] sm:text-[26px] lg:text-[34px] leading-tight text-navy break-words sm:truncate">{header.title}</h1>
              {header.subtitle && (
                <p className="hidden sm:block mt-0.5 text-[16px] lg:text-[15px] text-ink-muted truncate">{header.subtitle}</p>
              )}
            </>
          )}
        </div>

        <PatientSearch compact={Boolean(header)} />
        {showBell && <PhysicianAlertsBox />}
        <UserMenu />
        <div className="hidden 2xl:block shrink-0 pl-2">
          <p className="text-[11px] leading-[1.5] font-medium uppercase tracking-[0.2em] text-navy">
            Better health
            <br />
            gives you more
            <br />
            good days.
          </p>
          <div className="mt-1.5 h-px w-7 bg-navy/70" />
        </div>
      </div>
    </header>
  );
}

/* --- Patient search, server-side ----------------------------------- */

interface SearchHit {
  id: string;
  firstName: string;
  lastName: string;
  externalId: string | null;
}

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), ms);
    return () => window.clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

function PatientSearch({ compact }: { compact: boolean }) {
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const term = useDebounced(text.trim(), 250);

  const hits = useQuery({
    queryKey: ['header-search', term],
    queryFn: () => api.get<{ data: SearchHit[]; total: number }>(`/patients${qs({ search: term, pageSize: 6 })}`),
    enabled: term.length >= 2,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const go = (path: string) => {
    setOpen(false);
    setText('');
    navigate(path);
  };

  return (
    <div ref={box} className={`relative hidden md:block shrink-0 ${compact ? 'w-[200px]' : 'w-[260px] xl:w-[350px]'}`}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (text.trim()) go(`/patients${qs({ search: text.trim() })}`);
        }}
      >
        <label className="flex items-center gap-3 h-[46px] rounded-md border border-hairline bg-white px-4 focus-within:border-forest/50">
          <IconSearch size={18} className="text-navy shrink-0" />
          <input
            type="search"
            value={text}
            onChange={(event) => {
              setText(event.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={(event) => event.key === 'Escape' && setOpen(false)}
            placeholder="Search patients..."
            aria-label="Search patients"
            className="w-full bg-transparent text-[14.5px] text-navy placeholder:text-ink-muted focus:outline-none"
          />
        </label>
      </form>

      {open && term.length >= 2 && (
        <div className="mcos-pop absolute z-40 mt-2 w-full rounded-xl border border-hairline bg-white shadow-[0_12px_32px_rgba(20,23,28,0.12)] overflow-hidden">
          {hits.isLoading ? (
            <p className="px-4 py-3 text-sm text-ink-muted">Searching...</p>
          ) : !hits.data || hits.data.data.length === 0 ? (
            <p className="px-4 py-3 text-sm text-ink-muted">No patient matches "{term}"</p>
          ) : (
            <ul className="py-1">
              {hits.data.data.map((hit) => (
                <li key={hit.id}>
                  <button
                    type="button"
                    onClick={() => go(`/patients/${hit.id}`)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-mint"
                  >
                    <span className="grid h-8 w-8 place-items-center rounded-full bg-[#eef0ee] font-display text-[13px] text-navy">
                      {`${hit.firstName.charAt(0)}${hit.lastName.charAt(0)}`.toUpperCase()}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold text-navy truncate">
                        {hit.firstName} {hit.lastName}
                      </span>
                      {hit.externalId && <span className="block text-xs text-ink-muted">{hit.externalId}</span>}
                    </span>
                  </button>
                </li>
              ))}
              {hits.data.total > hits.data.data.length && (
                <li className="border-t border-hairline">
                  <button
                    type="button"
                    onClick={() => go(`/patients${qs({ search: term })}`)}
                    className="w-full px-4 py-2.5 text-left text-sm font-semibold text-forest hover:bg-mint"
                  >
                    See all {hits.data.total} matches
                  </button>
                </li>
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/* --- Physician Alerts ---------------------------------------------- */

type Module = 'LABS' | 'NEW_PATIENT' | 'RETAIN' | 'TREATMENT_PLAN' | 'REPORT' | 'CREDENTIALS';
type Priority = 'CRITICAL' | 'HIGH' | 'NORMAL' | 'LOW';

export interface ClinicNotification {
  id: string;
  module: Module;
  priority: Priority;
  title: string;
  body: string;
  href: string;
  patientId: string | null;
  readAt: string | null;
  createdAt: string;
}

export const MODULE_LABEL: Record<Module, string> = {
  LABS: 'Labs',
  NEW_PATIENT: 'New patient',
  RETAIN: 'RETAIN',
  TREATMENT_PLAN: 'Treatment plan',
  REPORT: 'Report',
  CREDENTIALS: 'Credentials',
};

export const PRIORITY_STYLE: Record<Priority, { label: string; tile: string; dot: string }> = {
  CRITICAL: { label: 'Critical', tile: 'bg-alert-soft text-alert', dot: 'bg-alert' },
  HIGH: { label: 'High', tile: 'bg-risk-orange-soft text-risk-orange', dot: 'bg-risk-orange' },
  NORMAL: { label: 'New', tile: 'bg-mint text-forest', dot: 'bg-forest' },
  LOW: { label: 'Low', tile: 'bg-[#eef0ee] text-ink-muted', dot: 'bg-ink-muted' },
};

/** "5 min ago", "3 h ago", "2 days ago". */
export function timeAgo(value: string): string {
  const minutes = Math.round((Date.now() - new Date(value).getTime()) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  return `${days} ${days === 1 ? 'day' : 'days'} ago`;
}

export function useNotificationCounts(enabled = true) {
  return useQuery({
    queryKey: ['notification-counts'],
    queryFn: () => api.get<NotificationCounts>('/notifications/counts'),
    enabled,
    refetchInterval: 120_000,
    retry: false,
  });
}

/**
 * The Physician Alerts box, where the bell used to be.
 *
 * Brian, 26 September 2026: "Consider replacing the bell icon with the dot
 * with a clearly labelled Physician Alerts box, so it's obvious at a glance."
 * The count is the open lab alerts at high priority or above ("patients with
 * abnormal/critical lab values requiring attention", 23 Sept), the list the
 * dashboard's Physician Alerts card used to show. It opens into that list,
 * and every row goes to where the value is dealt with. The socket refreshes
 * it the moment an alert opens or resolves (`lib/realtime.ts`).
 */
function PhysicianAlertsBox() {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const alerts = useQuery({
    queryKey: ['physician-alerts'],
    queryFn: () =>
      api.get<{ notifications: ClinicNotification[]; counts: NotificationCounts }>(
        '/notifications?module=LABS&minPriority=HIGH&limit=100',
      ),
    refetchInterval: 120_000,
    retry: false,
  });

  const markRead = useMutation({
    mutationFn: (id: string) => api.post(`/notifications/${id}/read`),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['physician-alerts'] });
      void queryClient.invalidateQueries({ queryKey: ['notification-counts'] });
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  const items = alerts.data?.notifications ?? [];
  const count = items.length;
  const criticalCount = items.filter((item) => item.priority === 'CRITICAL').length;
  const critical = criticalCount > 0;
  const retain = alerts.data?.counts.byModule.RETAIN ?? 0;
  const shown = count >= 100 ? '99+' : String(count);

  return (
    <div ref={box} className="relative shrink-0">
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={alerts.data ? `Physician alerts, ${count} open${critical ? ', critical' : ''}` : 'Physician alerts'}
        onClick={() => setOpen((was) => !was)}
        className={`flex h-[46px] items-center gap-2 rounded-md border px-2.5 text-left transition-colors sm:px-3 ${
          count > 0
            ? critical
              ? 'border-alert-line bg-alert-soft/60 hover:bg-alert-soft'
              : 'border-alert-line bg-white hover:bg-alert-soft/40'
            : 'border-hairline bg-white hover:bg-ink/[0.03]'
        }`}
      >
        <span aria-hidden="true" className={`shrink-0 ${count > 0 ? 'text-alert' : 'text-forest'}`}>
          {count > 0 ? <IconAlert size={18} /> : <IconCheck size={17} />}
        </span>
        {/* A phone has room for a short label and the count bubble; wider
            screens name the box in full and give the count its own line, which
            keeps the header as narrow as the mockup's bell allowed. */}
        <span className="whitespace-nowrap text-[14px] font-medium text-navy sm:hidden">Alerts</span>
        <span
          aria-hidden="true"
          className={`grid h-[22px] min-w-[22px] place-items-center rounded-full px-1.5 text-[12px] font-semibold leading-none sm:hidden ${
            count > 0 ? 'bg-alert text-white' : 'bg-[#eef0ee] text-ink-muted'
          }`}
        >
          {alerts.data ? shown : '-'}
        </span>
        <span className="hidden whitespace-nowrap leading-tight sm:block">
          <span className="block text-[14px] font-medium text-navy">Physician Alerts</span>
          <span className={`block text-[12.5px] ${count > 0 ? 'font-semibold text-alert' : 'text-ink-muted'}`}>
            {!alerts.data
              ? alerts.error
                ? 'Not available'
                : 'Loading...'
              : count > 0
                ? `${shown} open${criticalCount > 0 ? `, ${criticalCount} critical` : ''}`
                : 'None open'}
          </span>
        </span>
        <IconChevronDown size={15} className={`-ml-0.5 hidden shrink-0 text-navy transition-transform sm:block ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Physician alerts"
          className="mcos-pop max-sm:fixed max-sm:inset-x-4 max-sm:top-[76px] max-sm:w-auto absolute right-0 z-40 mt-2 w-[min(420px,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-hairline bg-white shadow-[0_16px_40px_rgba(20,23,28,0.14)]"
        >
          <div className="border-b border-hairline px-5 pt-4 pb-3">
            <p className="font-display text-[18px] text-navy" style={{ fontWeight: 560 }}>
              Physician alerts
            </p>
            <p className="text-[12.5px] text-[#4d6072]">Abnormal and critical lab values, most urgent first</p>
          </div>
          <div className="max-h-[440px] overflow-y-auto max-sm:max-h-[calc(100dvh-240px)]">
            {alerts.isLoading ? (
              <p className="px-5 py-6 text-sm text-ink-muted">Loading...</p>
            ) : alerts.error ? (
              <p className="px-5 py-6 text-[13.5px] text-[#4d6072]">Alerts could not be loaded. Try again in a moment.</p>
            ) : items.length === 0 ? (
              <p className="px-5 py-6 text-[13.5px] text-[#4d6072]">Nothing needs a physician right now.</p>
            ) : (
              <ul className="divide-y divide-hairline/70">
                {items.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => {
                        if (!item.readAt) markRead.mutate(item.id);
                        setOpen(false);
                        navigate(item.href);
                      }}
                      className={`flex w-full items-start gap-3 px-5 py-3 text-left hover:bg-paper ${item.readAt ? '' : 'bg-mint/35'}`}
                    >
                      <span
                        className={`mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full ${
                          item.priority === 'CRITICAL' ? 'bg-alert text-white' : 'bg-alert-soft text-alert'
                        }`}
                      >
                        <IconAlert size={16} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2">
                          <span className="min-w-0 text-[13.5px] font-semibold text-navy">{item.title}</span>
                          <span className="ml-auto whitespace-nowrap text-[11.5px] text-[#6b7a88]">{timeAgo(item.createdAt)}</span>
                        </span>
                        <span className="mt-0.5 block text-[13px] leading-snug text-[#4d6072]">{item.body}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {retain > 0 && (
            <Link
              to="/retain"
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 border-t border-hairline px-5 py-3 text-[13.5px] text-navy hover:bg-mint"
            >
              <IconHeart size={17} className="text-forest" />
              {retain} RETAIN {retain === 1 ? 'patient needs' : 'patients need'} review
              <IconChevron size={16} className="ml-auto text-navy" />
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

/* --- The signed-in clinician ---------------------------------------- */

function UserMenu() {
  const { user, logout, can } = useAuth();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const initials = useMemo(() => {
    const parts = (user?.name ?? '').replace(/^dr\.?\s+/i, '').split(/\s+/).filter(Boolean);
    return `${parts[0]?.charAt(0) ?? ''}${parts[parts.length - 1]?.charAt(0) ?? ''}`.toUpperCase() || '-';
  }, [user?.name]);

  if (!user) return null;
  const clinicRole = can('ADMIN', 'CLINICIAN', 'STAFF');

  return (
    <div ref={box} className="relative shrink-0">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account menu, ${user.name}`}
        onClick={() => setOpen((was) => !was)}
        className="flex items-center gap-3 rounded-full p-0.5 sm:py-1 sm:pl-1 sm:pr-2 hover:bg-ink/[0.03]"
      >
        <span className="grid h-11 w-11 sm:h-14 sm:w-14 place-items-center rounded-full bg-mint font-display text-[17px] sm:text-[20px] text-navy">
          {initials}
        </span>
        <span className="hidden md:block text-left">
          <span className="block text-[16px] text-navy leading-tight">{user.name}</span>
          <span className="block text-[14px] text-ink-muted leading-tight mt-0.5">{ROLE_LABEL[user.role]}</span>
        </span>
        <IconChevronDown size={18} className="hidden md:block text-navy ml-1" />
      </button>

      {open && (
        <div role="menu" className="mcos-pop absolute right-0 z-40 mt-2 w-60 rounded-xl border border-hairline bg-white py-1.5 shadow-[0_12px_32px_rgba(20,23,28,0.12)]">
          <div className="px-4 py-2.5 border-b border-hairline">
            <p className="text-sm font-semibold text-navy truncate">{user.name}</p>
            <p className="text-xs text-ink-muted truncate">{user.tenant.name}</p>
          </div>
          {clinicRole && (
            <MenuLink to="/application" icon={IconCalendar} onClick={() => setOpen(false)}>
              My application
            </MenuLink>
          )}
          {can('ADMIN') && (
            <MenuLink to="/settings" icon={IconSettings} onClick={() => setOpen(false)}>
              Settings
            </MenuLink>
          )}
          <button
            type="button"
            role="menuitem"
            onClick={() => void logout()}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-navy hover:bg-mint"
          >
            <IconLogout size={17} className="text-ink-muted" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function MenuLink({
  to,
  icon: Icon,
  children,
  onClick,
}: {
  to: string;
  icon: IconComponent;
  children: ReactNode;
  onClick: () => void;
}) {
  return (
    <Link to={to} role="menuitem" onClick={onClick} className="flex items-center gap-3 px-4 py-2.5 text-sm text-navy hover:bg-mint">
      <Icon size={17} className="text-ink-muted" />
      {children}
    </Link>
  );
}
